import React, { useState } from 'react';

import { avatarApis } from '@apis/dashboard/avatarApis';
import defaultAvatar from '@assets/img/icons/default-avatar.svg';
import { useMutation } from '@hooks/useMutation';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeMuiStyles } from '@styles';
import clsx from 'clsx';
import { useField } from 'formik';
import ImageUploading, { ImageListType } from 'react-images-uploading';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumnCenter,
  },
  helperText: {
    ...common.middleText,
    ...common.baseButton,
    color: theme.palette.primary.main,
    marginTop: 15,
  },
  backdrop: {
    position: 'absolute',
    zIndex: 999999,
  },
}));

export interface AvatarInputProps {
  name: string;
  imageWidth: number;
  imageHeight: number;
  round?: boolean;
  helperText?: string;
  onChanged?: (avatarUrl: string) => void;
  disabled?: boolean;
}

export const AvatarInput: React.FC<AvatarInputProps> = (props) => {
  const { name, imageWidth, imageHeight, round = false, helperText, onChanged, disabled } = props;

  const classes = useStyles();

  const [image, setImage] = useState<ImageListType>([]);

  const [field, , helper] = useField<string>(name);
  const value = field.value ?? defaultAvatar;

  const upload = useMutation(async (list: ImageListType) => {
    const file = list[0]?.file;
    if (file) {
      const response = await avatarApis.post('/avatar/', {
        body: {
          avatar_data: '{}',
          avatar_file: file,
        },
      });
      const { result } = response['200']!;
      helper.setValue(result);
      if (onChanged) {
        onChanged(result);
      }
      return result;
    }
    return undefined;
  });

  return (
    <ImageUploading
      value={image}
      onChange={(l) => {
        setImage(l);
        upload.mutate(l);
      }}
    >
      {({ onImageUpload }) => {
        return (
          <div className={clsx(classes.container)}>
            <div
              style={{
                width: `${imageWidth}px`,
                height: `${imageHeight}px`,
                borderRadius: round ? Math.min(imageWidth, imageHeight) / 2 : 0,
                overflow: `${round ? 'hidden' : ''}`,
                backgroundImage: `url(${value || ''})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                cursor: `${disabled ? 'default' : 'pointer'}`,
              }}
              onClick={!disabled ? onImageUpload : undefined}
            >
              <Backdrop className={classes.backdrop} open={upload.isLoading}>
                <CircularProgress color="primary" />
              </Backdrop>
            </div>
            <div className={classes.helperText} onClick={!disabled ? onImageUpload : undefined}>
              {helperText}
            </div>
          </div>
        );
      }}
    </ImageUploading>
  );
};
